import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Home, FileText, Plus, BarChart3, CheckSquare } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';
import { projectsApi } from '../lib/api';
import { useLogger } from '../utils/logger';

interface NavItem {
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  isCreate?: boolean;
}

const navItems: NavItem[] = [
  {
    path: '/projects',
    icon: Home,
    label: 'Проекты',
  },
  {
    path: '/projects',
    icon: FileText,
    label: 'Протоколы',
  },
  {
    path: '/create',
    icon: Plus,
    label: 'Создать',
    isCreate: true,
  },
  {
    path: '/checklists',
    icon: CheckSquare,
    label: 'Чек-листы',
  },
  {
    path: '/stats',
    icon: BarChart3,
    label: 'KPI',
  },
];

export function BottomNavigation() {
  const logger = useLogger('BottomNavigation');
  const navigate = useNavigate();
  const location = useLocation();
  const { hapticFeedback } = useTelegram();

  useEffect(() => {
    logger.componentMount({ pathname: location.pathname });
    return () => {
      logger.componentUnmount();
    };
  }, []);

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      logger.apiCall('GET', '/projects');
      try {
        const data = await projectsApi.list();
        logger.apiSuccess('GET', '/projects', data);
        return data;
      } catch (error: any) {
        logger.apiError('GET', '/projects', error);
        throw error;
      }
    },
  });

  const isActive = (path: string, label: string): boolean => {
    if (label === 'Протоколы') {
      // Для кнопки "Протоколы" активна, если мы на странице протоколов проекта
      return location.pathname.includes('/protocols') && !location.pathname.match(/\/protocols\/[^/]+$/);
    }
    if (label === 'Проекты') {
      // Для кнопки "Проекты" активна только на главной странице проектов
      return location.pathname === '/projects';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavClick = (path: string, label: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    logger.userAction('nav_click', { path, label });
    hapticFeedback('light');
    
    if (label === 'Протоколы') {
      // Для кнопки "Протоколы" ведем на страницу всех протоколов пользователя
      logger.debug('Navigating to user protocols');
      navigate('/protocols');
    } else {
      navigate(path);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-tg-secondary-bg border-t border-gray-200 dark:border-tg-hint/10 z-50 pb-safe">
      <div className="grid grid-cols-5 h-16 max-w-lg mx-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path, item.label);
          
          // Специальная кнопка "Создать"
          if (item.isCreate) {
            return (
              <button
                key={item.path}
                onClick={(e) => handleNavClick(item.path, item.label, e)}
                className="flex flex-col items-center justify-center relative"
                type="button"
              >
                <div className="absolute -top-5">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center shadow-lg
                    transition-all duration-200 active:scale-95
                    ${active 
                      ? 'bg-blue-600' 
                      : 'bg-blue-500'
                    }
                  `}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <span className={`
                  text-[10px] font-medium mt-8
                  ${active ? 'text-blue-500' : 'text-gray-400'}
                `}>
                  {item.label}
                </span>
              </button>
            );
          }
          
          return (
            <button
              key={item.path}
              onClick={(e) => handleNavClick(item.path, item.label, e)}
              className="flex flex-col items-center justify-center transition-all duration-200 active:scale-95"
              type="button"
            >
              <Icon className={`
                w-6 h-6 transition-colors
                ${active ? 'text-blue-500' : 'text-gray-400'}
              `} />
              {active && (
                <div className="w-1 h-1 rounded-full bg-blue-500 mt-1" />
              )}
              <span className={`
                text-[10px] font-medium mt-1
                ${active ? 'text-blue-500' : 'text-gray-400'}
              `}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

